import React, { useEffect, useState } from "react";
import { Play, CheckCircle2, Camera, X, Loader2, MapPin, AlertTriangle, UserX, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Loading, Field } from "@/components/common";
import { Btn, Textarea, TextInput, Select } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";
import { SignaturePad } from "@/components/SignaturePad";
import { FormField } from "@/components/FormRenderer";

export function JobCompletion({ visitId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [declaration, setDeclaration] = useState(false);
  const [followUp, setFollowUp] = useState({ title: "", type: "Follow-up visit", due: "" });
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState([]);
  const [forms, setForms] = useState([]);
  const [formAnswers, setFormAnswers] = useState({});

  const load = async () => {
    const r = await api.get(`/visits/${visitId}/full`);
    setData(r.data); setNotes(r.data.visit.completion_notes || "");
    try {
      const fr = await api.get(`/visits/${visitId}/forms`);
      setForms(fr.data.forms || []);
      const init = {};
      (fr.data.forms || []).forEach((f) => { init[f.id] = fr.data.submissions[f.id]?.answers || {}; });
      setFormAnswers(init);
    } catch { /* forms optional */ }
  };
  useEffect(() => { load(); }, [visitId]);
  if (!data) return <Modal open onOpenChange={onClose} title="Loading job…"><Loading /></Modal>;

  const setAnswer = (formId, fieldId, val) => setFormAnswers((a) => ({ ...a, [formId]: { ...(a[formId] || {}), [fieldId]: val } }));

  const v = data.visit;
  const req = data.required_completion_fields || [];
  const inProgress = v.status === "In Progress";

  const start = async () => { try { await api.post(`/visits/${visitId}/start`); toast.success("Job started"); load(); } catch (e) { toast.error(apiError(e)); } };

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    files.forEach((f) => { const r = new FileReader(); r.onload = () => setPhotos((p) => [...p, r.result]); r.readAsDataURL(f); });
  };

  const complete = async (override = false) => {
    setSaving(true); setProblems([]);
    try {
      for (const f of forms) {
        await api.post(`/visits/${visitId}/forms/${f.id}`, { answers: formAnswers[f.id] || {}, allow_incomplete: override });
      }
      await api.post(`/visits/${visitId}/complete`, {
        completion_notes: notes, actual_finish: new Date().toISOString(), signature, photos,
        declaration, follow_up_title: followUp.title || null, follow_up_type: followUp.type, follow_up_due: followUp.due || null,
        override, reason: override ? "Manager override of completion rules" : "",
      });
      toast.success("Job completed"); onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 400 && d?.problems) { setProblems(d.problems); toast.error("Completion requirements not met"); }
      else if (e.response?.status === 400 && typeof d === "string") { setProblems([d]); toast.error(d); }
      else toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const mark = async (path, label) => {
    const reason = window.prompt(`${label} — enter a reason:`);
    if (reason === null) return;
    try { await api.post(`/visits/${visitId}/${path}`, { reason }); toast.success(label); onDone(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open onOpenChange={onClose} title={v.job_title || v.service_name} wide testId="job-completion-modal"
      description={`${v.visit_ref} · ${v.client_name} · ${data.site?.site_name || ""}`}>
      <div className="flex items-center justify-between">
        <StatusBadge status={v.status} />
        {data.site?.gps_lat && (
          <a target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${data.site.gps_lat},${data.site.gps_lng}`}
            className="text-sm text-seq-green font-medium inline-flex items-center gap-1"><MapPin size={14} /> Navigate</a>
        )}
      </div>

      {data.site?.access_instructions && <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-600"><b>Access:</b> {data.site.access_instructions}</div>}
      {data.site?.health_safety_notes && <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-amber-700"><b>H&amp;S:</b> {data.site.health_safety_notes}</div>}

      {!inProgress && v.status !== "Completed" ? (
        <Btn onClick={start} className="w-full" data-testid="start-job-btn"><Play size={16} /> Start job (record arrival)</Btn>
      ) : (
        <>
          {v.actual_start && <div className="text-xs text-slate-400">Started at {new Date(v.actual_start).toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })}</div>}
          <Field label={`Completion notes ${req.includes("completion_notes") ? "*" : ""}`}>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="completion-notes" placeholder="Summary of work carried out, findings, recommendations…" />
          </Field>

          <div>
            <label className="text-sm font-medium text-slate-700">Photographs {req.includes("photographs") && "*"}</label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200">
                  <img src={p} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"><X size={11} /></button>
                </div>
              ))}
              {photos.length < 6 && (
                <label className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer text-slate-400 hover:border-seq-green" data-testid="photo-upload">
                  <Camera size={20} /><input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
                </label>
              )}
            </div>
          </div>

          <Field label={`Client signature ${req.includes("client_signature") ? "*" : ""}`}>
            <SignaturePad onChange={setSignature} />
          </Field>

          {forms.map((f) => (
            <div key={f.id} className="rounded-lg border border-slate-200 p-3" data-testid={`completion-form-${f.id}`}>
              <div className="font-medium text-slate-700 text-sm">{f.title}</div>
              {f.description && <p className="text-xs text-slate-400 mb-1">{f.description}</p>}
              <div className="space-y-3 mt-2">
                {f.fields.map((fld) => (
                  <FormField key={fld.id} field={fld} value={(formAnswers[f.id] || {})[fld.id]} onChange={(fid, val) => setAnswer(f.id, fid, val)} />
                ))}
              </div>
            </div>
          ))}

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600 font-medium">Raise a follow-up action (optional)</summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Field label="Action title"><TextInput value={followUp.title} onChange={(e) => setFollowUp({ ...followUp, title: e.target.value })} /></Field>
              <Field label="Due date"><TextInput type="date" value={followUp.due} onChange={(e) => setFollowUp({ ...followUp, due: e.target.value })} /></Field>
            </div>
          </details>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} data-testid="declaration-check" className="rounded" />
            I confirm the work was carried out and the above record is accurate.
          </label>

          {problems.length > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-1">
              {problems.map((p, i) => <div key={i} className="text-sm text-red-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {p}</div>)}
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-between border-t border-slate-100 pt-3">
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={() => mark("client-unavailable", "Client unavailable")} className="text-amber-600"><UserX size={15} /> Client unavailable</Btn>
              <Btn variant="secondary" onClick={() => mark("unable-to-complete", "Unable to complete")} className="text-red-600"><Ban size={15} /> Unable to complete</Btn>
            </div>
            <Btn onClick={() => complete()} disabled={saving} data-testid="complete-job-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Complete job
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}
